import * as React from 'react'
import styled from 'styled-components'

/**
 * SecretField — FR-019 / T055 / NFR-005.
 *
 * A masked input for credential fields (API keys, tokens, passwords).
 * - Renders as type="password" by default; reveals on demand.
 * - When `hasStoredValue` is true and the input is empty, shows a
 *   "stored — click to change" placeholder so operators know a value exists.
 * - Never logs or exposes the raw value outside of the input element.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const Label = styled.label`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.theme.white};
  opacity: 0.6;
`

const RevealButton = styled.button`
  font-size: 11px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${(p) => p.theme.accent};
  font-family: 'NunitoSansSemiBold';

  &:hover { opacity: 0.8; }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${(p) => p.theme.white}, 0 0 0 4px ${(p) => p.theme.accent};
    border-radius: 2px;
  }
`

const Input = styled.input`
  padding: 8px 12px;
  border-radius: ${(p) => p.theme.radius};
  border: 1px solid ${(p) => p.theme.input.default};
  background-color: ${(p) => p.theme.pageBack};
  color: ${(p) => p.theme.white};
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  font-family: monospace;

  &:focus { outline: none; border-color: ${(p) => p.theme.accent}; }
  &::placeholder { opacity: 0.4; font-family: inherit; }
  &:disabled { opacity: 0.4; }
`

export type SecretFieldProps = {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  /** True when a value is already stored server-side (so empty field = "use stored"). */
  hasStoredValue?: boolean
  disabled?: boolean
  placeholder?: string
}

export function SecretField({
  id,
  label,
  value,
  onChange,
  hasStoredValue,
  disabled,
  placeholder,
}: SecretFieldProps) {
  const [revealed, setRevealed] = React.useState(false)

  const showPlaceholder = hasStoredValue && value === ''
    ? '●●●●●●●● stored — type to replace'
    : (placeholder ?? '')

  return (
    <Wrapper>
      <LabelRow>
        <Label htmlFor={id}>{label}</Label>
        <RevealButton
          type="button"
          onClick={() => setRevealed((r) => !r)}
          aria-label={revealed ? `Hide ${label}` : `Reveal ${label}`}
          tabIndex={0}
        >
          {revealed ? 'Hide' : 'Reveal'}
        </RevealButton>
      </LabelRow>
      <Input
        id={id}
        type={revealed ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={showPlaceholder}
        disabled={disabled}
        autoComplete="new-password"
        data-lpignore="true"
        aria-describedby={hasStoredValue && !value ? `${id}-stored-hint` : undefined}
      />
      {hasStoredValue && !value ? (
        <span
          id={`${id}-stored-hint`}
          style={{ fontSize: 11, opacity: 0.5, color: 'inherit' }}
        >
          Leave blank to keep the existing value.
        </span>
      ) : null}
    </Wrapper>
  )
}

export default SecretField
